import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../lib/auth";
import { Bike, User, Building2, Handshake, Wrench, Users as UsersIcon, Factory, Eye, EyeOff, ChevronRight } from "lucide-react";
import Logo from "../components/Logo";
import { useForceDarkTheme } from "../lib/theme";
import GoogleAuthButton from "../components/auth/GoogleAuthButton";
import { passkeySupported } from "../lib/passkey";
import api from "../lib/api";

/**
 * Phase 3.49 — Registrierungsseite zeigt B2C als Default.
 * B2B/Vermittler nur sichtbar wenn der User explizit über einen Deep-Link
 * vom Homepage-Footer kommt (``?role=b2b`` / ``?role=vermittler``).
 * Dienstleister haben ihre eigene Seite ``/register/service-provider``.
 */

// Feb-2026 (User-Wunsch): „Professioneller Zugang" als übersichtliche
// Zeilen-Liste. Händler (B2B) steht jetzt ganz oben in dieser Liste
// (Inline-Rollenauswahl), gefolgt von Dienstleister/Hersteller mit eigenem
// Onboarding-Flow. Clubs / Vereine wurde nach oben zu „Ich registriere mich als"
// verschoben.
const PRO_ROLES = [
    { to: "/register/service-provider", icon: Wrench, title: "Dienstleister / Lieferanten", desc: "Werkstatt · Bank · Versicherung · Garantiegeber · Lieferanten · …", testid: "reg-role-dienstleister" },
    { to: "/register/service-provider?branch=hersteller", icon: Factory, title: "Hersteller / Importeure", desc: "Marken-Hersteller · Generalimporteure", testid: "reg-role-hersteller-importeur" },
];

export default function RegisterPage() {
    useForceDarkTheme();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const initialRole = searchParams.get("role") || "b2c";
    const [role, setRole] = useState(initialRole);
    // Feb-2026 (User-Wunsch 26 May v3): Vorbefüllung aus Query-Parametern —
    // damit der anonyme Probefahrt-Anfrager seine bereits eingegebenen Daten
    // beim Account-Sign-Up nicht erneut tippen muss.
    const [form, setForm] = useState({
        email: searchParams.get("email") || "",
        password: "",
        password_repeat: "",
        first_name: searchParams.get("first_name") || "",
        last_name: searchParams.get("last_name") || "",
        company_name: "",
        phone: searchParams.get("phone") || "",
        city: "",
        address: "",
        // Jun-2026 (User-Wunsch) — getrennte Anschrift-Felder (Händler).
        postal_code: "",
        street: "",
        house_number: "",
        site_code: "",
        use_address_as_listing_location: true,
        whatsapp_consent: false,
    });
    // Standort-Code-Auto-Vorschlag bleibt aktiv (UI in Registrierung entfernt → Pflege in Stammdaten).
    const [siteCodeTouched] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [acceptedDealerTerms, setAcceptedDealerTerms] = useState(false);
    const { register } = useAuth();
    const navigate = useNavigate();

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    // Jun-2026 (User-Wunsch) — Standort-Code-Vorschlag aus den ersten 3 Buchstaben
    // des Straßennamens (Großbuchstaben), solange der Code nicht manuell geändert wurde.
    const updateStreet = (v) => {
        setForm((p) => {
            const next = { ...p, street: v };
            if (!siteCodeTouched) {
                const letters = (v || "").toUpperCase().replace(/[^A-ZÄÖÜ]/g, "");
                next.site_code = letters.slice(0, 3);
            }
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!acceptedTerms) {
            setError("Bitte AGB und Datenschutzerklärung akzeptieren, um fortzufahren.");
            return;
        }
        if (role === "b2b" && !acceptedDealerTerms) {
            setError("Bitte AVV und Händler-Nutzungsbedingungen akzeptieren, um fortzufahren.");
            return;
        }
        if (form.password !== form.password_repeat) {
            setError("Die Passwörter stimmen nicht überein.");
            return;
        }
        setLoading(true);
        const name = `${form.first_name} ${form.last_name}`.trim();
        const payload = { ...form, name, role };
        if (role === "b2b") {
            payload.accept_dealer_terms = acceptedDealerTerms;
        }
        if (role !== "b2b") {
            delete payload.company_name;
            delete payload.street;
            delete payload.house_number;
            delete payload.postal_code;
            delete payload.site_code;
            delete payload.use_address_as_listing_location;
        }
        const res = await register(payload);
        // Jun-2026 (User-Wunsch) — Akquise-Quelle (src/theme aus „Was kann
        // TwoWheels?") nach erfolgreicher Registrierung melden (Best-Effort,
        // blockiert die Navigation nie).
        if (res.ok) {
            const src = searchParams.get("src");
            if (src) {
                try {
                    await api.post("/track/registration-source", { src, theme: searchParams.get("theme") || null });
                } catch (_e) { /* Tracking ist optional */ }
            }
        }
        setLoading(false);
        if (res.ok) {
            // Jun-2026 (User-Wunsch) — Händler nach Registrierung zum Dashboard,
            // wo der „Konto in Prüfung / E-Mail bestätigen"-Hinweis erscheint.
            navigate(role === "b2b" ? "/willkommen" : (passkeySupported() ? "/passkeys?welcome=1" : "/dashboard"));
        } else setError(res.error);
    };

    return (
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-12" data-testid="register-page">
            <div className="w-full max-w-xl">
                <Link to="/" className="flex items-center gap-2 justify-center mb-8">
                    <Logo className="contents" boxClass="w-12 h-12" iconClass="w-7 h-7" deClass="text-black/40" />
                </Link>

                <div className="bg-white border border-gray-200 rounded-sm p-8">
                    <h1 className="font-display text-3xl font-black tracking-tight mb-1">{t("auth.register_title")}</h1>
                    <p className="text-gray-600 text-sm mb-6">{t("auth.register_subtitle")}</p>

                    <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-testid="reg-role-section-label">
                        Ich registriere mich als
                    </div>
                    <div className="grid grid-cols-3 gap-2 mb-5" data-testid="reg-role-tiles">
                        <button
                            type="button"
                            onClick={() => setRole("b2c")}
                            className={`flex flex-col items-center gap-2 p-4 rounded-sm border-2 transition-colors ${role === "b2c" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid="reg-role-b2c"
                        >
                            <User className="w-5 h-5" />
                            <div className="text-xs font-bold uppercase tracking-wider text-center leading-tight">{t("auth.role_b2c")}</div>
                            <div className="text-[10px] text-gray-500 text-center leading-tight">{t("auth.role_b2c_hint")}</div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setRole("vermittler")}
                            className={`flex flex-col items-center gap-2 p-4 rounded-sm border-2 transition-colors ${role === "vermittler" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-gray-400"}`}
                            data-testid="reg-role-vermittler"
                        >
                            <Handshake className="w-5 h-5" />
                            <div className="text-xs font-bold uppercase tracking-wider text-center leading-tight">Vermittler / Verkäufer</div>
                            <div className="text-[10px] text-gray-500 text-center leading-tight">Tippgeber · Verkäufer · Punkte sammeln</div>
                        </button>
                        {/* Feb-2026 (User-Wunsch): Clubs / Vereine nach oben zu
                            „Ich registriere mich als" verschoben — eigener
                            Onboarding-Flow bleibt erhalten (Link). */}
                        <Link
                            to="/register/service-provider?branch=clubs_vereine"
                            className="flex flex-col items-center gap-2 p-4 rounded-sm border-2 border-gray-200 hover:border-gray-400 transition-colors"
                            data-testid="reg-role-club"
                        >
                            <UsersIcon className="w-5 h-5" />
                            <div className="text-xs font-bold uppercase tracking-wider text-center leading-tight">Clubs / Vereine</div>
                            <div className="text-[10px] text-gray-500 text-center leading-tight">Motorrad-Club · Bike-Verein · IG</div>
                        </Link>
                    </div>

                    {/* Phase 4.4 — Zusätzliche Rollen. Feb-2026 (User-Wunsch):
                        Händler (B2B) wurde hierher nach unten verschoben und steht
                        als erste Zeile über „Dienstleister / Lieferanten" (Inline-
                        Rollenauswahl, kein eigener Onboarding-Flow). */}
                    <div className="mb-2 mt-1 text-[10px] font-bold uppercase tracking-wider text-gray-500" data-testid="reg-pro-section-label">
                        Professioneller Zugang
                    </div>
                    <div className="space-y-2 mb-6" data-testid="reg-extra-role-tiles">
                        <button
                            type="button"
                            onClick={() => setRole("b2b")}
                            className={`group w-full flex items-center gap-3 p-3 rounded-sm border-2 transition-colors text-left ${role === "b2b" ? "border-[#111827] bg-[#FFCC00]/20" : "border-gray-200 hover:border-[#111827] hover:bg-[#FFCC00]/10"}`}
                            data-testid="reg-role-b2b"
                        >
                            <div className="w-10 h-10 shrink-0 rounded-sm bg-[#FFCC00]/20 flex items-center justify-center group-hover:bg-[#FFCC00]/40 transition-colors">
                                <Building2 className="w-5 h-5 text-[#111827]" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-bold text-[#111827]">{t("auth.role_b2b_full")}</div>
                                <div className="text-[11px] text-gray-500 leading-tight">{t("auth.role_b2b_hint")}</div>
                            </div>
                            {role === "b2b"
                                ? <span className="text-[10px] font-bold uppercase tracking-wider text-[#111827] bg-[#FFCC00] px-2 py-0.5 rounded-sm shrink-0" data-testid="reg-role-b2b-selected">Ausgewählt</span>
                                : <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#111827] shrink-0 transition-colors" />}
                        </button>
                        {PRO_ROLES.map(({ to, icon: Icon, title, desc, testid }) => (
                            <Link
                                key={testid}
                                to={to}
                                className="group flex items-center gap-3 p-3 rounded-sm border-2 border-gray-200 hover:border-[#111827] hover:bg-[#FFCC00]/10 transition-colors"
                                data-testid={testid}
                            >
                                <div className="w-10 h-10 shrink-0 rounded-sm bg-[#FFCC00]/20 flex items-center justify-center group-hover:bg-[#FFCC00]/40 transition-colors">
                                    <Icon className="w-5 h-5 text-[#111827]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold text-[#111827]">{title}</div>
                                    <div className="text-[11px] text-gray-500 leading-tight">{desc}</div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#111827] shrink-0 transition-colors" />
                            </Link>
                        ))}
                    </div>

                    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("auth.email")}</Label>
                            <Input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} required autoComplete="email" className="mt-1.5 h-11 rounded-sm" data-testid="reg-email" />
                        </div>
                        <div className="md:col-span-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("auth.password_min")}</Label>
                            <div className="relative mt-1.5">
                                <Input
                                    type={showPassword ? "text" : "password"}
                                    minLength={6}
                                    value={form.password}
                                    onChange={(e) => update("password", e.target.value)}
                                    required
                                    autoComplete="new-password"
                                    className="h-11 rounded-sm pr-11"
                                    data-testid="reg-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((v) => !v)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-gray-500 hover:text-[#111827] focus:outline-none rounded-sm"
                                    aria-label={showPassword ? "Passwort verbergen" : "Passwort anzeigen"}
                                    data-testid="reg-password-toggle"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {/* Jun-2026 (User-Wunsch) — Passwort-Wiederholung zum Abgleich */}
                        <div className="md:col-span-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Passwort wiederholen</Label>
                            <Input
                                type={showPassword ? "text" : "password"}
                                minLength={6}
                                value={form.password_repeat}
                                onChange={(e) => update("password_repeat", e.target.value)}
                                required
                                autoComplete="new-password"
                                className={`mt-1.5 h-11 rounded-sm ${form.password_repeat && form.password !== form.password_repeat ? "border-red-400 focus-visible:ring-red-400" : ""}`}
                                data-testid="reg-password-repeat"
                            />
                            {form.password_repeat && form.password !== form.password_repeat && (
                                <p className="text-[11px] text-red-600 mt-1" data-testid="reg-password-mismatch">Die Passwörter stimmen nicht überein.</p>
                            )}
                        </div>
                        {/* Jun-2026 (User-Wunsch) — Vor- und Nachname getrennt */}
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Vorname</Label>
                            <Input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required className="mt-1.5 h-11 rounded-sm" data-testid="reg-first-name" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nachname</Label>
                            <Input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required className="mt-1.5 h-11 rounded-sm" data-testid="reg-last-name" />
                        </div>
                        <div className={role === "b2b" ? "" : "md:col-span-2"}>
                            <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Handynummer</Label>
                            <Input value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="reg-phone" />
                            {role !== "b2b" && form.phone.trim() && (
                                <label className="flex items-start gap-2 mt-2 cursor-pointer" data-testid="reg-whatsapp-consent-row">
                                    <input
                                        type="checkbox"
                                        checked={!!form.whatsapp_consent}
                                        onChange={(e) => update("whatsapp_consent", e.target.checked)}
                                        className="mt-0.5 w-4 h-4 accent-[#25D366] shrink-0"
                                        data-testid="reg-whatsapp-consent-checkbox"
                                    />
                                    <span className="text-[11px] text-gray-600 leading-snug">
                                        Ich stimme zu, dass ich unter dieser Nummer auch per <span className="font-bold text-[#128C7E]">WhatsApp</span> kontaktiert werden darf. (Optional, jederzeit im Profil widerrufbar.)
                                    </span>
                                </label>
                            )}
                        </div>
                        {role === "b2b" && (
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("auth.company_name")}</Label>
                                <Input value={form.company_name} onChange={(e) => update("company_name", e.target.value)} required className="mt-1.5 h-11 rounded-sm" data-testid="reg-company" />
                            </div>
                        )}
                        {role === "b2b" ? (
                            <>
                                {/* Jun-2026 (User-Wunsch) — Straße + Hausnummer getrennt */}
                                <div className="md:col-span-2 grid grid-cols-[1fr,120px] gap-3">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Straße</Label>
                                        <Input value={form.street} onChange={(e) => updateStreet(e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="reg-street" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Hausnr.</Label>
                                        <Input value={form.house_number} onChange={(e) => update("house_number", e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="reg-house-number" />
                                    </div>
                                </div>
                                {/* Jun-2026 (User-Wunsch) — PLZ + Stadt getrennt */}
                                <div className="md:col-span-2 grid grid-cols-[120px,1fr] gap-3">
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">PLZ</Label>
                                        <Input value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="reg-postal-code" />
                                    </div>
                                    <div>
                                        <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Stadt</Label>
                                        <Input value={form.city} onChange={(e) => update("city", e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="reg-city" />
                                    </div>
                                </div>
                                {/* Standort-Code & „Anschrift als Fahrzeugstandort" entfernt (Jun-2026, User-Wunsch):
                                    werden ausschließlich in den Stammdaten verwaltet & angezeigt. */}
                            </>
                        ) : (
                            <>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("auth.city")}</Label>
                                    <Input value={form.city} onChange={(e) => update("city", e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="reg-city" />
                                </div>
                                <div>
                                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t("auth.address")}</Label>
                                    <Input value={form.address} onChange={(e) => update("address", e.target.value)} className="mt-1.5 h-11 rounded-sm" data-testid="reg-address" />
                                </div>
                            </>
                        )}

                        {error && <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-sm" data-testid="reg-error">{error}</div>}

                        {/* Phase 3.56 — AGB + Datenschutz kombinierte Pflicht-Zustimmung */}
                        <label className="md:col-span-2 flex items-start gap-2.5 cursor-pointer bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="reg-terms-row">
                            <input
                                type="checkbox"
                                checked={acceptedTerms}
                                onChange={(e) => setAcceptedTerms(e.target.checked)}
                                className="mt-0.5 w-4 h-4 accent-[#111827]"
                                data-testid="reg-terms-checkbox"
                            />
                            <span className="text-xs text-gray-700 leading-relaxed">
                                Ich habe die <a href="/legal/agb" target="_blank" rel="noreferrer" className="font-bold underline">AGB</a> und die <a href="/legal/datenschutz" target="_blank" rel="noreferrer" className="font-bold underline">Datenschutzerklärung</a> gelesen und stimme ihnen zu.
                            </span>
                        </label>

                        {role === "b2b" && (
                            <label className="md:col-span-2 flex items-start gap-2.5 cursor-pointer bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="reg-dealer-terms-row">
                                <input
                                    type="checkbox"
                                    checked={acceptedDealerTerms}
                                    onChange={(e) => setAcceptedDealerTerms(e.target.checked)}
                                    className="mt-0.5 w-4 h-4 accent-[#111827]"
                                    data-testid="reg-dealer-terms-checkbox"
                                />
                                <span className="text-xs text-gray-700 leading-relaxed">
                                    Als Händler akzeptiere ich den <a href="/legal/avv" target="_blank" rel="noreferrer" className="font-bold underline">Auftragsverarbeitungsvertrag (AVV)</a> und die <a href="/legal/haendler-nutzungsbedingungen" target="_blank" rel="noreferrer" className="font-bold underline">Händler-Nutzungsbedingungen</a>.
                                </span>
                            </label>
                        )}

                        <Button type="submit" disabled={loading || !acceptedTerms || (role === "b2b" && !acceptedDealerTerms)} className="md:col-span-2 w-full h-12 bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider disabled:opacity-50" data-testid="reg-submit">
                            {loading ? t("auth.submit_register_loading") : t("auth.submit_register")}
                        </Button>
                    </form>

                    {/* Phase 3.56 — Social Login: Google (Apple/Facebook folgen später) */}
                    <div className="my-5 flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200" />
                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400">oder</div>
                        <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    <GoogleAuthButton mode="register" acceptedTerms={acceptedTerms} />

                    <div className="mt-6 pt-6 border-t border-gray-100 text-sm text-center text-gray-600">
                        {t("auth.have_account")} <Link to="/login" className="font-bold underline decoration-2 underline-offset-4 text-[#111827]">{t("auth.login_link")}</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
